import {
  STEPPER_GLOBAL_OPTIONS,
  StepperSelectionEvent
} from '@angular/cdk/stepper';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  OnInit,
  output,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatStep, MatStepper } from '@angular/material/stepper';
import { filter, tap, throttleTime } from 'rxjs';
import { BaseAuthComponent } from '../../abstract-components/base-auth.component';
import { AuthModule } from '../../auth.module';

type StepState = 'number' | 'edit' | 'done';

@Component({
  selector: 'signup-stepper',
  imports: [AuthModule],
  providers: [
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: { displayDefaultIndicatorType: false }
    }
  ],
  templateUrl: './signup-stepper.component.html',
  styleUrl: './signup-stepper.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SignupStepperComponent
  extends BaseAuthComponent
  implements OnInit, AfterViewInit
{
  private readonly _stepper = viewChild.required(MatStepper);

  stepChange = output<number>();

  states: StepState[] = [];

  isLoginFinishedWithOtp$ = this._authFacade.isLoginFinishedWithOtp$;

  onStepNext$ = this._eventService.events$.pipe(
    throttleTime(500),
    filter(event => event.type === 'GO_TO_NEXT_STEP'),
    tap({
      next: () => {
        this.goToStep((this._stepper().selectedIndex || 0) + 1);
      }
    })
  );

  onStepPrevious$ = this._eventService.events$.pipe(
    filter(event => event.type === 'GO_TO_PREVIOUS_STEP'),
    tap({
      next: event => {
        this.goToStep(
          typeof event.value === 'number'
            ? event.value
            : (this._stepper().selectedIndex || 0) - 1
        );
      }
    })
  );

  ngOnInit() {
    this._listenToStepChangeEvents();
  }

  ngAfterViewInit() {
    this._initStates();
  }

  private _initStates(): void {
    const stepper = this._stepper();
    const count = stepper.steps.length;

    this.states =
      count > 0
        ? ['edit', ...Array<StepState>(Math.max(count - 1, 0)).fill('number')]
        : [];

    stepper.selectedIndex = 0;
    stepper.steps.forEach(s => (s.completed = false));

    this._cdRef.markForCheck();
  }

  emitStepChange(step: StepperSelectionEvent): void {
    this._updateStates(step.selectedIndex);
    this.stepChange.emit(step.selectedIndex);
  }

  private _updateStates(activeIndex: number): void {
    const stepper = this._stepper();
    for (let i = 0; i < this.states.length; i++) {
      this.states[i] =
        i < activeIndex ? 'done' : i === activeIndex ? 'edit' : 'number';
    }

    stepper.steps.forEach((step: MatStep, i: number) => {
      if (i > activeIndex) step.completed = false;
    });

    this._cdRef.markForCheck();
  }

  goToStep(stepIndex: number): void {
    const stepper = this._stepper();
    stepper.linear = false;
    stepper.selectedIndex = stepIndex;
    this._updateStates(stepIndex);
    this._cdRef.detectChanges();

    setTimeout(() => {
      stepper.linear = true;
    });
  }

  goToNextStep(): void {
    const selectedIndex = this._stepper().selectedIndex;
    selectedIndex < this._stepper().steps.length &&
      this.goToStep(this._stepper().selectedIndex + 1);
  }

  goToPreviousStep(): void {
    const selectedIndex = this._stepper().selectedIndex;
    selectedIndex > 0 && this.goToStep(this._stepper().selectedIndex - 1);
  }

  private _listenToStepChangeEvents(): void {
    this.isLoginFinishedWithOtp$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap(() => {
          const stepper = this._stepper();
          if (stepper.selectedIndex === 0) {
            this.goToStep(1);
          }
        })
      )
      .subscribe();
  }
}
